import { debounce, isEqual } from 'lodash';
import { useEffect, useMemo, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useLocation, useNavigate } from 'react-router';
import useUnitProvider from '/@/core/context/unit';
import settings from '/@/core/settings';
import {
    setAllCrops,
    setAllFarmingTypes,
    setAllSoilTypes,
    setCalculationResult,
    setCampaignFrom,
    setCampaigns,
    setCampaignTo,
    setClay,
    setDensity,
    setFarmingType,
    setOrganicMaterial,
    setSelectedCampaign,
    setSelectedCampaigns,
    setSoiltype,
} from '/@/store/carbon-calculator/actions';
import {
    getCalculationResult,
    getCampaignFrom,
    getCampaignsOptions,
    getCampaignTo,
    getClay,
    getCrops,
    getDensity,
    getOrganicMaterial,
    getRequiredFieldsComplete,
    getSelectedCampaigns,
    getSelectedFarmingOption,
    getSoilType,
} from '/@/store/carbon-calculator/selectors';
import { fetchHelper } from '/@/store/session/actions';
import { mpTrackEvent } from '/@/store/tracking/mixpanel-events';
import {
    addCrop,
    areInputsValid,
    buildRequestBody,
    type Campaign,
    type CropOption,
    hasValidCrop,
    removeCrop,
    type SeasonResult,
    type SelectedCampaigns,
    setCrop,
    setCropYield,
} from './utils';

/** The crops-coefficients API id for corn; picking it offers a corn recommendation. */
const CORN_CROP_ID = 3;

/**
 * Loads the calculator catalogs (farming types, soil types, crops, campaigns) and
 * resets the calculator store when the view unmounts.
 */
export function useCarbonCalculatorCatalogs(fieldZoneIds?: string) {
    const dispatch = useDispatch();
    const campaignsOptions = useMemo(() => getCampaignsOptions(), []);

    useEffect(() => {
        dispatch(setCampaigns(campaignsOptions));

        const fetchFarmingTypes = async () => {
            const data = await fetchHelper(settings.urls.carbonCalculator.farmingTypes, { method: 'get' });
            dispatch(setAllFarmingTypes(data.data.response));
        };
        const fetchCoefficientsCrops = async () => {
            const data = await fetchHelper(settings.urls.carbonCalculator.cropsCoefficients, { method: 'get' });
            dispatch(setAllCrops(data.data.response));
        };
        fetchFarmingTypes();
        fetchCoefficientsCrops();
    }, [dispatch, campaignsOptions]);

    useEffect(() => {
        const fetchSoilTypes = async () => {
            const url = `${settings.urls.carbonCalculator.soilTypes}?zone_ids=${fieldZoneIds}`;
            const data = await fetchHelper(url, { method: 'get' });
            dispatch(setAllSoilTypes(data.data.response));
        };
        fetchSoilTypes();
    }, [dispatch, fieldZoneIds]);

    useEffect(
        () => () => {
            dispatch(setCampaignFrom(undefined));
            dispatch(setCampaignTo(undefined));
            dispatch(setSoiltype(undefined));
            dispatch(setOrganicMaterial(undefined));
            dispatch(setDensity(undefined));
            dispatch(setClay(undefined));
            dispatch(setFarmingType(undefined));
            dispatch(setCalculationResult([]));
            dispatch(setSelectedCampaigns({}));
        },
        [dispatch]
    );

    return campaignsOptions;
}

/**
 * Posts the simulation and stores the results. Recalculates (debounced) whenever a
 * required input changes, every input is in range and at least one campaign has a
 * complete crop.
 */
export function useCarbonCalculation(temperature: number | string) {
    const dispatch = useDispatch();
    const navigate = useNavigate();
    const location = useLocation();
    const units = useUnitProvider();

    const campaignFrom = useSelector(getCampaignFrom);
    const campaignTo = useSelector(getCampaignTo);
    const farmingType = useSelector(getSelectedFarmingOption);
    const soilType = useSelector(getSoilType);
    const clay = useSelector(getClay);
    const organicMaterial = useSelector(getOrganicMaterial);
    const density = useSelector(getDensity);
    const selectedCampaigns: SelectedCampaigns = useSelector(getSelectedCampaigns);
    const requiredFieldsComplete = useSelector(getRequiredFieldsComplete);
    const calculationResult: SeasonResult[] = useSelector(getCalculationResult);

    const [isCalculating, setIsCalculating] = useState(false);
    const [hasError, setHasError] = useState(false);
    // Only the latest request may store its results; an older one can resolve after it.
    const lastRequest = useRef(0);

    const fetchCarbonResults = async () => {
        const request = ++lastRequest.current;
        const body = buildRequestBody(selectedCampaigns);
        const id = new URLSearchParams(location.search).get('id');

        const paramsObj: Record<string, string> = {
            farming: farmingType,
            soil_clay: clay,
            temperature: String(temperature),
            organic_material: organicMaterial,
            apparent_density: density,
            variable: 'y',
        };
        if (id) paramsObj.id = id;
        const queryParams = new URLSearchParams(paramsObj);
        const url = `${settings.urls.carbonCalculator.calculateAr}?${queryParams.toString()}`;

        setIsCalculating(true);
        try {
            const data = await fetchHelper(url, {
                method: 'post',
                body: JSON.stringify(body),
            });
            // fetchHelper resolves on a 400 too, with the error as the payload.
            const response = data.data?.response;
            if (!Array.isArray(response?.seasons)) throw new Error('Unexpected carbon calculator response');
            if (request !== lastRequest.current) return;

            const seasons = response.seasons.map((season: SeasonResult) => {
                return {
                    ...season,
                    co: {
                        simulated: season.co.simulated / units.toArea(1, 4).value,
                        fixed: season.co.fixed / units.toArea(1, 4).value,
                    },
                };
            });

            setHasError(false);
            dispatch(setCalculationResult(seasons));

            // Set ID to update DB results
            if (!id && response.id) navigate({ search: `id=${response.id}` }, { replace: true });
        } catch (error) {
            if (request !== lastRequest.current) return;
            console.error(error);
            setHasError(true);
        } finally {
            if (request === lastRequest.current) setIsCalculating(false);
        }
    };

    // The debounced call fires later, so it must read the latest props/location, not the ones
    // captured when it was scheduled.
    const fetchRef = useRef(fetchCarbonResults);
    fetchRef.current = fetchCarbonResults;
    const debounceFetchCarbonResults = useMemo(() => debounce(() => fetchRef.current(), 500), []);
    useEffect(() => () => debounceFetchCarbonResults.cancel(), [debounceFetchCarbonResults]);

    const canCalculate = (campaigns: SelectedCampaigns) =>
        requiredFieldsComplete &&
        areInputsValid({ temperature, clay, organicMaterial, density }) &&
        hasValidCrop(campaigns);

    /** Recalculates if `campaigns` (the ones about to be stored, by default the current) can be. */
    const calculate = (campaigns: SelectedCampaigns = selectedCampaigns) => {
        if (canCalculate(campaigns)) debounceFetchCarbonResults();
    };

    // biome-ignore lint/correctness/useExhaustiveDependencies: recalculate only when these inputs change
    useEffect(() => {
        calculate();
    }, [campaignFrom, campaignTo, farmingType, soilType, temperature, clay, density, organicMaterial]);

    const prevCalculationResult = useRef(calculationResult);
    useEffect(() => {
        if (!isEqual(prevCalculationResult.current, calculationResult) && calculationResult.length > 0) {
            mpTrackEvent('Sustainable Rotations Simulations Generated', undefined);
        }
        prevCalculationResult.current = calculationResult;
    }, [calculationResult]);

    return { calculate, isCalculating, hasError };
}

interface UseCampaignCropsOptions {
    onCalculate: (campaigns: SelectedCampaigns) => void;
    onCropSelected: (crop: string) => void;
    onYieldEntered: () => void;
}

/** Crop editing within a campaign; see the campaign crop helpers in `./utils`. */
export function useCampaignCrops({ onCalculate, onCropSelected, onYieldEntered }: UseCampaignCropsOptions) {
    const dispatch = useDispatch();
    const selectedCampaigns: SelectedCampaigns = useSelector(getSelectedCampaigns);
    const crops: CropOption[] = useSelector(getCrops);

    const update = (season: string, change: (campaign: Campaign) => Campaign) => {
        const campaign = selectedCampaigns[season];
        if (!campaign) return;
        const newCampaign = change(campaign);
        dispatch(setSelectedCampaign({ season, newCampaign }));
        onCalculate({ ...selectedCampaigns, [season]: newCampaign });
    };

    const changeCrop = (season: string, index: number, option: CropOption) => {
        update(season, (campaign) => setCrop(campaign, index, option));
        onCropSelected(option.value === CORN_CROP_ID ? 'corn' : '');
    };

    const changeYield = (season: string, index: number, value: number | string) => {
        update(season, (campaign) => setCropYield(campaign, index, value));
        if (value !== '') onYieldEntered();
    };

    const addCropToCampaign = (season: string) => update(season, addCrop);

    const removeCropFromCampaign = (season: string, index: number) =>
        update(season, (campaign) => removeCrop(campaign, index, crops));

    return { changeCrop, changeYield, addCropToCampaign, removeCropFromCampaign };
}
